import { useState, useEffect, Fragment } from "react";
import { formatDate } from "@libs/utils";
import { Box, TextField } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { H1, AccessCheck, Loading, SubmitButton, ConfirmButton, Overlay } from "@components";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { UserRoleEnum } from "features/users/types";
import { useNotification } from "@hooks/useNotifications";
import { useBreadcrumbs } from "@hooks/useBreadcrumbs/useBreadcrumbs";
import { ReportTable } from "../components/ReportTable";
import { useFetchReport } from "../hooks/useFetchReport";
import { statisticsService } from "../services/statistics-service";

export const ReportPage = () => {
  const { setBreadcrumbsMap } = useBreadcrumbs();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewReport = id === "new-report";
  const snapshotVersionId = location.state?.newReport?.snapshotVersionId;

  const { report, isLoading } = useFetchReport(id!, isNewReport, location.state?.newReport);
  const { showError, showSuccess } = useNotification();

  const [reportName, setReportName] = useState("");
  const [isSubmitProcessing, setIsSubmitProcessing] = useState(false);
  const [keepDialogOpen, setKeepDialogOpen] = useState(false);

  useEffect(() => {
    if (isNewReport && !location.state?.newReport) {
      navigate(`/statistics/`);
      showError("No data provided for new report.");
    }
  }, []);

  useEffect(() => {
    if (report?.name) {
      setBreadcrumbsMap({ id: report.name });
    }
  }, [report]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportName(event.target.value);
  };

  if (isLoading) {
    return <Loading />;
  }

  if (report === undefined || !id) {
    return null;
  }

  const onDownloadReportJSON = () => {
    const jsonString = JSON.stringify(report, null, 2);

    const blob = new Blob([jsonString], { type: "application/json" });

    // Create a temporary link element
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${report?.name ?? id}`;

    link.click();

    // Clean up the URL object
    URL.revokeObjectURL(link.href);
  };

  const onSubmitReport = async () => {
    try {
      setIsSubmitProcessing(true);

      await statisticsService.submitReport({ name: reportName, snapshotVersionId });

      navigate(`/statistics/`);
      showSuccess("Report submitted successfully.");
    } catch (error) {
      const message =
        error?.response?.data?.errors?.name?.join(" ") ||
        `Failed to submit report: ${error?.response?.data?.title ? error?.response?.data?.title : "."}`;
      showError(message);
      console.error(error);
    }
    setIsSubmitProcessing(false);
    setKeepDialogOpen(false);
  };

  const onCloseSubmitReport = () => {
    setKeepDialogOpen(false);
  };

  console.log("ReportPage", report);

  return (
    <AccessCheck minAccessLevel={UserRoleEnum.Manager}>
      <Overlay isVisible={isSubmitProcessing} />
      <H1 label={isNewReport ? "Report preview:" : `Report ${report.name}:`}>
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* {isNewReport ? null : <DeleteButton id={id} deleteService={statisticsService.deleteReport} label="Report" />} */}
          <SubmitButton onClick={onDownloadReportJSON}>
            Download .json &nbsp;
            <ArrowDownTrayIcon width={16} height={16} />
          </SubmitButton>
        </Box>
      </H1>

      {isNewReport ? (
        <Box />
      ) : (
        <p>
          {/* Бэк поменял формат даты, чтобы не было багов этот хак решает проблему обратной совместимости с предыдущим форматом даты */}
          Created on: {report.content.date.includes(" ") ? report.content.date : formatDate(report.content.date)}
          <br />
          {report.content.userEmail}
        </p>
      )}
      <ReportTable report={report} />

      <Box sx={{ pt: 3, pb: 5 }}>
        {isNewReport ? (
          <AccessCheck minAccessLevel={UserRoleEnum.Manager} showAlert={false}>
            <ConfirmButton
              forceOpen={keepDialogOpen}
              dialogTitle=""
              dialogContent={
                <>
                  <Box sx={{ mb: 2 }}>Submit report.</Box>
                  <TextField
                    label="Report name"
                    variant="outlined"
                    id="report-name"
                    size="small"
                    onChange={handleChange}
                    sx={{ minWidth: 300 }}
                  />
                </>
              }
              onConfirm={onSubmitReport}
              onClose={onCloseSubmitReport}
            >
              <SubmitButton onClick={() => {}} isProcessing={isSubmitProcessing}>
                Submit Report
              </SubmitButton>
            </ConfirmButton>
          </AccessCheck>
        ) : null}
      </Box>
    </AccessCheck>
  );
};
